'use client';

import { amplitudeSendEvent, getScreenName } from '@musinsa/frontend-utils';
import { Button, IcBoldPlus } from '@musinsa/mds';
import { useDialog } from '@musinsa/mds-ui';
import { SERVICE, useDeviceInfo } from '@musinsa/packages-device-detection';
import { track } from '@musinsa/packages-event-snowplow';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { getPaths, pathsMusinsa } from '../../common';
import { useNavigate } from '../../common/hooks/useNavigate';
import { useUserInfo } from '../../common/hooks/useUserInfo';
import { curationsFactory } from '../../query-factory/curations.factory';
import { SNOWPLOW_SCHEME } from '../../snowplow';
import { CurationRegisterTypeBottomSheet } from './CurationRegisterTypeBottomSheet';

interface Props {
  className?: string;
}

const trackCurationCreationEnter = () => {
  track({
    schema: SNOWPLOW_SCHEME.CLICK_BUTTON,
    data: {
      page_name: 'curator_shop_manage',
      section_name: 'CurationCreation',
      section_title: 'CurationCreation',
      button_id: 'enter_page',
      button_name: '큐레이션 생성 진입',
      position: -1,
    },
  });
};

export const MyShopCreateCurationButton = ({ className }: Props) => {
  const { navigate } = useNavigate();
  const queryClient = useQueryClient();
  const dialog = useDialog();
  const { service } = useDeviceInfo();
  const paths = getPaths(service);
  const { data: userInfo } = useUserInfo();
  const [registerTypeSheetOpen, setRegisterTypeSheetOpen] = useState(false);
  const curatorId = userInfo?.snsTitle ?? '';

  const openGoodsCurationCreate = () => {
    navigate(paths.uploadGoods());
  };

  /** 스냅 큐레이션 생성은 무신사 전용 (mss-curator 업로드 플로우) */
  const openSnapCurationCreate = () => {
    navigate(pathsMusinsa.uploadSnap());
  };

  const handleClick = async () => {
    const { isCreatable } = await queryClient.fetchQuery({
      ...curationsFactory.checkCurationCreatable(),
    });

    if (!isCreatable) {
      dialog.open({
        title: '큐레이션은 최대 100개까지만 등록할 수 있습니다.',
        message: '새로운 큐레이션을 등록하시려면 기존 큐레이션을 삭제해 주세요.',
        confirmButtonText: '확인',
        onConfirm: () => dialog.close(),
      });
      return;
    }

    if (service === SERVICE.MUSINSA) {
      amplitudeSendEvent({
        action_name: 'click',
        button_name: 'curation_upload_try',
        component_name: 'button',
        event_name: 'click_button',
        from: getScreenName(),
        id: curatorId,
        name: 'fab_button',
        section_name: 'curator_curationfeed',
      });
      setRegisterTypeSheetOpen(true);
      return;
    }

    if (service === SERVICE['29CM']) {
      trackCurationCreationEnter();
      openGoodsCurationCreate();
      return;
    }

    openGoodsCurationCreate();
  };

  return (
    <>
      <Button
        className={twMerge(
          'rounded-full w-[40px] h-[40px] flex items-center justify-center',
          className,
        )}
        onClick={handleClick}
      >
        <IcBoldPlus width={28} height={28} size="28px" strokeColor="white" className="shrink-0" />
      </Button>
      {service === SERVICE.MUSINSA ? (
        <CurationRegisterTypeBottomSheet
          open={registerTypeSheetOpen}
          onOpenChange={setRegisterTypeSheetOpen}
          onSelectGoods={() => {
            amplitudeSendEvent({
              action_name: 'click',
              button_name: 'curation_upload_select',
              component_name: 'button',
              event_name: 'click_button',
              from: getScreenName(),
              id: curatorId,
              section_name: 'curator_curationfeed',
              type: 'goods',
            });
            openGoodsCurationCreate();
          }}
          onSelectSnap={() => {
            amplitudeSendEvent({
              action_name: 'click',
              button_name: 'curation_upload_select',
              component_name: 'button',
              event_name: 'click_button',
              from: getScreenName(),
              id: curatorId,
              section_name: 'curator_curationfeed',
              type: 'snap',
            });
            openSnapCurationCreate();
          }}
        />
      ) : null}
    </>
  );
};
